import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'components/button';
import { CheckboxGroup } from 'components/checkbox-group/checkbox-group';
import { Icon } from 'components/icon/icon';
import { Checkbox } from 'components/checkbox/checkbox';

import { Popper } from '../core/popper';
import IcnCross from 'public/assets/icons/cross.svg';
import styles from './filter-button.module.scss';
import useOutsideClick from 'utils/use-outside-click';
import { useWindowSize } from 'utils/use-window-size';

export interface IFilterItem {
	id: number;
	label: string;
}

export interface IFilterButtonProps {
	title?: string;
	items: IFilterItem[];
	onChange: () => void;
	onSelectedItemsChanged: (items: IFilterItem[]) => void;
	selectedItems: IFilterItem[];
}

const MOBILE_SIZE = 768;

const FilterButton = (props: IFilterButtonProps): React.JSX.Element => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [hasChanges, setHasChanges] = useState<boolean>(false);

	const dropDownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLSpanElement>(null);
	const mobileMenuRef = useRef<HTMLDivElement>(null);

	useOutsideClick([dropDownRef, buttonRef, mobileMenuRef], () => setIsOpen(false), true);
	const windowSize = useWindowSize(100);

	useEffect(() => {
		if (!isOpen && hasChanges) {
			props.onChange();
		}

		if (isOpen) {
			setHasChanges(false);
		}

		if (isOpen && (windowSize.width || 0) < MOBILE_SIZE) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'visible';
		}
	}, [isOpen]);

	const onCheckboxValuesChange = (newItems: IFilterItem[], oldItems: IFilterItem[], isChecked: boolean): void => {
		setHasChanges(true);

		if (isChecked) {
			props.onSelectedItemsChanged([...oldItems, ...newItems]);
		} else {
			const filteredSelectedItems = oldItems.filter(
				(selectedItem) => selectedItem.id !== newItems.find((item) => item.id)?.id
			);
			props.onSelectedItemsChanged(filteredSelectedItems);
		}
	};

	const onMobileItemSelected = (item: IFilterItem): void => {
		if (props.selectedItems.some((selectedItem) => item.id === selectedItem.id)) {
			props.onSelectedItemsChanged(props.selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
		} else {
			props.onSelectedItemsChanged([...props.selectedItems, item]);
		}
	};

	return (
		<div
			className={styles.filterButton}
			aria-label={`${
				isOpen ? intl.formatMessage({ id: 'subsidy.status.open' }) : intl.formatMessage({ id: 'subsidy.status.closed' })
			}`}
		>
			<Popper
				id={props.title || ''}
				toggleButton={
					<span className={styles.button} ref={buttonRef}>
						<Button
							variant="secondary-light"
							title={props.title}
							onClick={() => {
								setIsOpen(!isOpen);
							}}
						/>
					</span>
				}
				isOpen={isOpen}
				zIndex={10}
			>
				<div className={styles.collapsibleMenu} ref={dropDownRef}>
					<div className={styles.content}>
						<div className={styles.filters}>
							<span className={styles.title}>{props.title}</span>
							<div>
								<CheckboxGroup
									groupName="propertyTypes"
									items={props.items}
									selectedItems={props.selectedItems}
									onChange={(items, isChecked): void =>
										onCheckboxValuesChange(
											items.map((item) => ({ id: Number(item.id), label: item.label })),
											props.selectedItems,
											isChecked
										)
									}
								/>
							</div>
							<div className={styles.bottomBar}>
								<span className={styles.filterButton}>
									{!!props.selectedItems.length && (
										<Button
											variant="tertiary-dark"
											title={intl.formatMessage(
												{ id: 'modules.newsOverview.deleteFilters' },
												{ amount: props.selectedItems.length }
											)}
											onClick={() => {
												props.onSelectedItemsChanged([]);
											}}
										/>
									)}
								</span>
								<Button
									variant="primary"
									title={intl.formatMessage({ id: 'filterButton.apply' })}
									onClick={() => {
										props.onChange();
										setIsOpen(false);
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			</Popper>
			<div className={clsx(styles.mobileMenu, isOpen ? styles.isOpen : '')} ref={mobileMenuRef}>
				<div className={styles.topBar}>
					<h3 id={props.title}>
						{props.title}
						<Button
							variant="tertiary-dark"
							icon={<Icon customIcon={<IcnCross />} size="13px" />}
							iconPosition="right"
							onClick={() => {
								props.onChange();
								setIsOpen(false);
							}}
						/>
					</h3>
				</div>
				<div className={styles.items}>
					{props.items.map((item, index) => (
						<div
							key={index}
							className={styles.item}
							role="button"
							tabIndex={0}
							onClick={() => {
								onMobileItemSelected(item);
							}}
							onKeyDown={() => {}}
						>
							<Checkbox
								name={item.label}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
								onChange={() => {}}
								title={item.label}
								checked={!!props.selectedItems.some((selectedItem) => selectedItem.id === item.id)}
							>
								{item.label}
							</Checkbox>
						</div>
					))}
				</div>
				<div className={styles.submit}>
					{!!props.selectedItems.length && (
						<div className={styles.deleteFiltersButton}>
							<Button
								variant="tertiary-dark"
								title={intl.formatMessage(
									{ id: 'modules.newsOverview.deleteFilters' },
									{ amount: props.selectedItems.length }
								)}
								onClick={() => {
									props.onSelectedItemsChanged([]);
								}}
							/>
						</div>
					)}

					<Button
						variant="primary"
						title={intl.formatMessage({ id: 'filterButton.apply' })}
						onClick={() => {
							props.onChange();
							setIsOpen(false);
						}}
						fullWidth
					/>
				</div>
			</div>
		</div>
	);
};

export { FilterButton };
